/* eslint-disable @typescript-eslint/no-explicit-any --
   Proposal detail handles complex nested line item/area data. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/format'
import { useMediaQuery } from '../hooks/useMediaQuery'
import ProposalDetailMobile from './ProposalDetailMobile'
import type {
  Proposal, ProposalArea, ProposalLineItem, Client, ActivityLog,
} from '@shared/types'
import { StatsSkeleton } from '../components/ui/Skeletons'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Breadcrumb } from '../components/ui/Breadcrumb'
import { CatalogPickerModal } from '../components/CatalogPickerModal'
import BrandingOverrideModal from '../components/BrandingOverrideModal'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  useDroppable,
  type DragEndEvent, type DragOverEvent,
} from '@dnd-kit/core'
import {
  SortableContext, verticalListSortingStrategy, useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useBrandingStore } from '../store/branding'
import { useToast } from '../components/ui/Toast'
import { Button } from '../components/ui/Button'

// ─── Types ───

interface ProposalPayment {
  id: string; proposal_id: string; label: string; amount_type: string
  amount_value: number | null; trigger_type: string; trigger_milestone_id: string | null
  trigger_date: string | null; calculated_amount: number | null
  status: string; sort_order: number
}

interface ProposalWithDetails extends Proposal {
  areas: ProposalArea[]
  line_items: ProposalLineItem[]
  payments: ProposalPayment[]
}

// ─── Helpers ───

function fmtMoney(n: number | null | undefined): string {
  return formatCurrency(n)
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return ''
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function timeAgo(d: string | null | undefined): string {
  if (!d) return ''
  const now = Date.now()
  const then = new Date(d).getTime()
  const diff = now - then
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return fmtDate(d)
}

const BRAND_COLORS: Record<string, string> = {
  Hikvision: '#cc0000', Ubiquiti: '#2563eb', Dell: '#007db8',
  Denon: '#1a1a2e', Sonance: '#2d5a27', 'JL Audio': '#8b0000',
  Sony: '#000000', Wavelynx: '#1e3a5f', 'Orion Access': '#d4880f',
  APC: '#ff6600', Monoprice: '#4b5563',
  Labor: '#6b7280', Default: '#4b5563',
}

function getBrandColor(brand?: string): string {
  if (!brand) return '#6b7280'
  return BRAND_COLORS[brand] || `hsl(${brand.charCodeAt(0) * 7 % 360}, 45%, 45%)`
}

const STATUS_OPTIONS = [
  'draft', 'sent', 'viewed', 'approved', 'rejected', 'expired', 'lost', 'changes_required',
] as const

// ─── Main Component ───

export default function ProposalDetail() {
  const isMobile = useMediaQuery('(max-width: 768px)')
  if (isMobile) return <ProposalDetailMobile />
  return <ProposalDetailDesktop />
}

function ProposalDetailDesktop() {
  const { slug } = useParams<{ slug: string }>()
  const { toast, toastError } = useToast()
  const [proposal, setProposal] = useState<ProposalWithDetails | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [activity, setActivity] = useState<ActivityLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeSection, setActiveSection] = useState('client')
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set())
  const [areaOptions, setAreaOptions] = useState<Record<string, number>>({})
  const [shareUrl, setShareUrl] = useState('')
  const [cloning, setCloning] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [addingArea, setAddingArea] = useState(false)
  const [newAreaName, setNewAreaName] = useState('')
  const [savingArea, setSavingArea] = useState(false)
  const [mobileTab, setMobileTab] = useState<'content' | 'sidebar'>('content')
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  // Assign client
  const [clientSearch, setClientSearch] = useState('')
  const [clientResults, setClientResults] = useState<Client[]>([])
  const [searchingClients, setSearchingClients] = useState(false)
  const [showClientPicker, setShowClientPicker] = useState(false)
  // Cover image
  const [coverUrl, setCoverUrl] = useState('')
  const [showCoverInput, setShowCoverInput] = useState(false)
  // VMS licensing
  const [vmsRules, setVmsRules] = useState<{ id: string; vms_type: string; vms_display_name: string }[]>([])
  const [vmsUpdating, setVmsUpdating] = useState(false)
  // Payment schedule
  const [addingPayment, setAddingPayment] = useState(false)
  const [paymentForm, setPaymentForm] = useState({ label: '', amount_type: 'percent', amount_value: 0, trigger_type: 'milestone', trigger_date: '' })
  const [savingPayment, setSavingPayment] = useState(false)
  const [showBranding, setShowBranding] = useState(false)

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})
  const mainRef = useRef<HTMLDivElement>(null)

  // Fetch proposal + client + activity
  useEffect(() => {
    if (!slug) return
    setLoading(true)

    api.get<ProposalWithDetails>(`/proposals/${slug}`)
      .then(async (data) => {
        setProposal(data)
        setExpandedAreas(new Set((data.areas ?? []).map((a) => a.id)))
        if (data.client_token) setShareUrl(`${window.location.origin}/p/${data.client_token}`)

        // Initialize area option selections
        const opts: Record<string, number> = {}
        for (const area of data.areas ?? []) {
          opts[area.id] = 0
        }
        setAreaOptions(opts)

        // Fetch client
        if (data.client_id) {
          try {
            const c = await api.get<Client>(`/clients/by-id/${data.client_id}`)
            setClient(c)
          } catch { /* no client data */ }
        }

        // Fetch activity
        try {
          const logs = await api.get<{ data: ActivityLog[] }>(
            `/activity-log?entity_type=proposal&entity_id=${data.id}&limit=10`
          )
          setActivity(logs.data ?? [])
        } catch { /* no activity */ }

        // Fetch VMS license rules
        try {
          const rules = await api.get<{ id: string; vms_type: string; vms_display_name: string; is_active: number }[]>('/vms-license-rules')
          setVmsRules(rules.filter(r => r.is_active))
        } catch { /* no vms rules */ }
      })
      .catch((err) => setError(err?.message || 'Failed to load proposal'))
      .finally(() => setLoading(false))
  }, [slug])

  // IntersectionObserver for anchor nav
  useEffect(() => {
    const scrollContainer = mainRef.current
    if (!scrollContainer || !proposal) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        }
      },
      { root: scrollContainer, rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    )

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [proposal])

  const setSectionRef = useCallback((id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el
  }, [])

  const toggleSection = (id: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const scrollTo = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // ─── Actions ───

  async function generateToken() {
    try {
      const res = await api.post<{ token: string; url: string }>(`/proposals/${slug}/generate-token`)
      setShareUrl(res.url)
      return res.url
    } catch { /* ignore */ }
    return null
  }

  async function previewClientView() {
    setMenuOpen(false)
    const url = shareUrl || await generateToken()
    if (url) window.open(url, '_blank')
  }

  // "Download PDF" prints the client-facing view — the same document the
  // customer sees — through the browser's own print-to-PDF. The tab is opened
  // synchronously: opening it after the await would be treated as an unrequested
  // popup and blocked.
  async function downloadPdf() {
    setMenuOpen(false)
    const tab = window.open('', '_blank')
    if (!tab) {
      toastError('Allow pop-ups for this site to download the PDF')
      return
    }
    try {
      const url = shareUrl || await generateToken()
      if (!url) throw new Error('Could not prepare the proposal document')
      tab.location.href = `${url}${url.includes('?') ? '&' : '?'}print=1`
      toast('Opening your print dialog — choose "Save as PDF"', 'info')
    } catch (err) {
      tab.close()
      toastError('Failed to prepare the PDF', err)
    }
  }

  async function handleClone() {
    setCloning(true)
    try {
      const cloned = await api.post<Proposal>(`/proposals/${slug}/clone`)
      window.location.href = `/proposals/${cloned.slug}`
    } catch { /* ignore */ }
    finally { setCloning(false) }
  }

  async function handleBrandingApply(brandingJson: string | null) {
    if (!proposal) return
    try {
      await api.patch(`/proposals/${slug}`, { branding_override: brandingJson })
      setProposal({ ...proposal, branding_override: brandingJson } as ProposalWithDetails)
    } catch { /* ignore */ }
    setShowBranding(false)
  }

  async function handleStatusChange(newStatus: string) {
    setMenuOpen(false)
    try {
      const updated = await api.patch<ProposalWithDetails>(`/proposals/${slug}`, { status: newStatus })
      setProposal((prev) => prev ? { ...prev, ...updated } : prev)
    } catch { /* ignore */ }
  }

  async function handleFieldSave(field: string, value: string) {
    try {
      await api.patch(`/proposals/${slug}`, { [field]: value })
      setProposal((prev) => prev ? { ...prev, [field]: value } : prev)
    } catch { /* ignore */ }
  }

  async function handleVmsTypeChange(vmsType: string | null) {
    setVmsUpdating(true)
    try {
      await api.patch<ProposalWithDetails>(`/proposals/${slug}`, { vms_type: vmsType })
      // Reload the full proposal to get updated line items from license recalculation
      const full = await api.get<ProposalWithDetails>(`/proposals/${slug}`)
      setProposal(full)
      setExpandedAreas(new Set((full.areas ?? []).map((a) => a.id)))
    } catch { /* ignore */ }
    finally { setVmsUpdating(false) }
  }

  async function handleAddArea() {
    if (!newAreaName.trim() || !proposal) return
    setSavingArea(true)
    try {
      const area = await api.post<ProposalArea>(`/proposals/${slug}/areas`, {
        name: newAreaName.trim(),
        sort_order: (proposal.areas ?? []).length,
      })
      setProposal((prev) => prev ? { ...prev, areas: [...(prev.areas ?? []), area] } : prev)
      setExpandedAreas((prev) => new Set([...prev, area.id]))
      setAreaOptions((prev) => ({ ...prev, [area.id]: 0 }))
      setNewAreaName('')
      setAddingArea(false)
    } catch { /* ignore */ }
    finally { setSavingArea(false) }
  }

  async function handleDeleteArea(areaSlug: string, areaId: string) {
    try {
      await api.delete(`/proposals/${slug}/areas/${areaSlug}`)
      setProposal((prev) => prev ? {
        ...prev,
        areas: (prev.areas ?? []).filter((a) => a.id !== areaId),
        line_items: (prev.line_items ?? []).filter((i) => i.area_id !== areaId),
      } : prev)
    } catch { /* ignore */ }
  }

  async function refreshProposal() {
    if (!slug) return
    try {
      const data = await api.get<ProposalWithDetails>(`/proposals/${slug}`)
      setProposal(data)
    } catch { /* ignore */ }
  }

  async function handleAddItem(areaId: string, item: {
    name: string; item_type: string; quantity: number; cost: number
    sell_price: number; description?: string; unit?: string
    manufacturer?: string; mpn?: string; msrp?: number
    catalog_item_id?: string; image_r2_key?: string
  }) {
    try {
      const existing = (proposal?.line_items ?? []).filter((i) => i.area_id === areaId)
      const res = await api.post<ProposalLineItem & { auto_labor_items?: ProposalLineItem[] }>(`/proposals/${slug}/items`, {
        area_id: areaId,
        item_type: item.item_type,
        name: item.name,
        description: item.description || '',
        quantity: item.quantity,
        unit: item.unit || 'ea',
        cost: item.cost,
        sell_price: item.sell_price,
        msrp: item.msrp,
        manufacturer: item.manufacturer,
        mpn: item.mpn,
        catalog_item_id: item.catalog_item_id,
        image_r2_key: item.image_r2_key,
        sort_order: existing.length,
      })

      // If auto-labor items were attached, refresh the full proposal to get accurate totals
      if (res.auto_labor_items && res.auto_labor_items.length > 0) {
        await refreshProposal()
      } else {
        setProposal((prev) => prev ? {
          ...prev,
          line_items: [...(prev.line_items ?? []), res],
          subtotal: prev.subtotal + item.sell_price * item.quantity,
          total: prev.total + item.sell_price * item.quantity,
        } : prev)
      }
    } catch { /* ignore */ }
  }

  async function handleApplyKit(kitId: string, areaSlug: string) {
    if (!slug) return
    try {
      await api.post(`/kits/${kitId}/apply`, { proposal_slug: slug, area_slug: areaSlug })
      await refreshProposal()
    } catch { /* ignore */ }
  }

  async function handleApplyTemplate(templateId: string, areaSlug: string) {
    if (!slug) return
    try {
      await api.post('/templates/apply', { template_id: templateId, proposal_slug: slug, area_slug: areaSlug })
      await refreshProposal()
    } catch { /* ignore */ }
  }

  async function handleSaveAsTemplate(areaSlug: string, name: string) {
    if (!slug) return
    try {
      await api.post('/templates/from-area', { proposal_slug: slug, area_slug: areaSlug, name })
    } catch { /* ignore */ }
  }

  async function handleDeleteItem(itemSlug: string, itemId: string) {
    const item = (proposal?.line_items ?? []).find((i) => i.id === itemId)
    try {
      await api.delete(`/proposals/${slug}/items/${itemSlug}`)
      setProposal((prev) => prev ? {
        ...prev,
        line_items: (prev.line_items ?? []).filter((i) => i.id !== itemId),
        subtotal: prev.subtotal - (item ? item.sell_price * item.quantity : 0),
        total: prev.total - (item ? item.sell_price * item.quantity : 0),
      } : prev)
    } catch { /* ignore */ }
  }

  function handleUpdateItem(itemId: string, updates: Partial<ProposalLineItem>) {
    setProposal((prev) => {
      if (!prev) return prev
      const newItems = (prev.line_items ?? []).map((i) =>
        i.id === itemId ? { ...i, ...updates } : i
      )
      // Recalculate subtotal if price/qty changed
      const subtotal = newItems
        .filter((i) => !i.is_optional)
        .reduce((s, i) => s + i.sell_price * i.quantity, 0)
      return { ...prev, line_items: newItems, subtotal }
    })
  }

  async function handleReorderItems(reordered: { id: string; sort_order: number; parent_item_id: string | null; combine_prices?: number }[]) {
    if (!slug) return
    // Optimistic update: apply new sort_order locally
    setProposal((prev) => {
      if (!prev) return prev
      const orderMap = new Map(reordered.map((r) => [r.id, r]))
      const newItems = (prev.line_items ?? []).map((i) => {
        const update = orderMap.get(i.id)
        if (!update) return i
        const patched: any = { ...i, sort_order: update.sort_order, parent_item_id: update.parent_item_id }
        if (update.combine_prices !== undefined) patched.combine_prices = update.combine_prices
        return patched
      })
      return { ...prev, line_items: newItems }
    })
    try {
      await api.patch(`/proposals/${slug}/items-reorder`, { items: reordered })
    } catch { /* revert on error by refreshing */
      await refreshProposal()
    }
  }

  // ─── Assign Client ───
  async function searchClients(query: string) {
    setClientSearch(query)
    if (query.length < 2) { setClientResults([]); return }
    setSearchingClients(true)
    try {
      const res = await api.get<{ data: Client[] }>(`/clients?search=${encodeURIComponent(query)}&limit=10`)
      setClientResults(res.data ?? [])
    } catch { setClientResults([]) }
    finally { setSearchingClients(false) }
  }

  async function assignClient(clientId: string) {
    try {
      await api.patch(`/proposals/${slug}`, { client_id: clientId })
      const c = await api.get<Client>(`/clients/by-id/${clientId}`)
      setClient(c)
      setProposal((prev) => prev ? { ...prev, client_id: clientId } : prev)
      setShowClientPicker(false)
      setClientSearch('')
      setClientResults([])
    } catch { /* ignore */ }
  }

  // ─── Cover Image ───
  async function handleCoverUpload(file: File) {
    if (!proposal) return
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('entity_type', 'proposal')
      formData.append('entity_id', proposal.id)
      const res = await api.upload<{ r2_key: string }>('/attachments', formData)
      await api.patch(`/proposals/${slug}`, { cover_image_r2_key: res.r2_key })
      setProposal((prev) => prev ? { ...prev, cover_image_r2_key: res.r2_key } : prev)
    } catch { /* ignore */ }
  }

  async function handleCoverUrl() {
    if (!coverUrl.trim()) return
    try {
      await api.patch(`/proposals/${slug}`, { cover_image_r2_key: coverUrl.trim() })
      setProposal((prev) => prev ? { ...prev, cover_image_r2_key: coverUrl.trim() } : prev)
      setShowCoverInput(false)
      setCoverUrl('')
    } catch { /* ignore */ }
  }

  // ─── Payment Schedule ───
  async function handleAddPayment() {
    if (!paymentForm.label.trim()) return
    setSavingPayment(true)
    try {
      const payment = await api.post<ProposalPayment>(`/proposals/${slug}/payments`, {
        label: paymentForm.label,
        amount_type: paymentForm.amount_type,
        amount_value: paymentForm.amount_value,
        trigger_type: paymentForm.trigger_type,
        trigger_date: paymentForm.trigger_date || null,
        sort_order: (proposal?.payments ?? []).length,
      })
      setProposal((prev) => prev ? { ...prev, payments: [...(prev.payments ?? []), payment] } : prev)
      setPaymentForm({ label: '', amount_type: 'percent', amount_value: 0, trigger_type: 'milestone', trigger_date: '' })
      setAddingPayment(false)
    } catch { /* ignore */ }
    finally { setSavingPayment(false) }
  }

  async function handleDeletePayment(paymentId: string) {
    try {
      await api.delete(`/proposals/${slug}/payments/${paymentId}`)
      setProposal((prev) => prev ? {
        ...prev,
        payments: (prev.payments ?? []).filter((p) => p.id !== paymentId),
      } : prev)
    } catch { /* ignore */ }
  }

  // ─── Margin Alerts (hook must be called before early returns) ───
  const { marginAlertThreshold, marginProposalWarningEnabled } = useBrandingStore()

  // ─── Render Guards ───

  if (loading) return <div className="page-enter" style={{ padding: 24 }}><StatsSkeleton count={4} /></div>
  if (error) return <div style={{ color: 'var(--text-secondary)', padding: 24 }}>{error}</div>
  if (!proposal) return <div style={{ color: 'var(--text-secondary)', padding: 24 }}>Proposal not found.</div>

  // ─── Computed ───

  const sortedAreas = [...(proposal.areas ?? [])].sort((a, b) => a.sort_order - b.sort_order)

  const getItemsForArea = (areaId: string) =>
    (proposal.line_items ?? []).filter((item) => item.area_id === areaId)

  const allItems = proposal.line_items ?? []
  const laborTotal = allItems
    .filter((i) => i.item_type === 'labor' && !i.is_optional)
    .reduce((s, i) => s + i.sell_price * i.quantity, 0)
  const partsTotal = allItems
    .filter((i) => i.item_type !== 'labor' && !i.is_optional)
    .reduce((s, i) => s + i.sell_price * i.quantity, 0)
  const taxExemptTotal = allItems
    .filter((i) => i.is_tax_exempt === 1 && !i.is_optional)
    .reduce((s, i) => s + i.sell_price * i.quantity, 0)
  const totalCost = allItems
    .filter((i) => !i.is_optional)
    .reduce((s, i) => s + i.cost * i.quantity, 0)
  const totalSell = allItems
    .filter((i) => !i.is_optional)
    .reduce((s, i) => s + i.sell_price * i.quantity, 0)
  const profit = totalSell - totalCost
  const marginPct = totalSell > 0 ? ((totalSell - totalCost) / totalSell) * 100 : 0

  // ─── Margin Alerts ───
  const lowMarginItems = allItems.filter((i) => {
    if (i.is_optional || i.sell_price <= 0) return false
    const m = ((i.sell_price - i.cost) / i.sell_price) * 100
    return m < marginAlertThreshold && m > 0
  })
  const belowCostItems = allItems.filter((i) => {
    if (i.is_optional || i.sell_price <= 0) return false
    return i.sell_price <= i.cost
  })

  // ─── Anchor items ───

  const anchorItems: { id: string; label: string; sub?: boolean }[] = [
    { id: 'client', label: client?.contact_name || client?.company_name || 'Client' },
    { id: 'cover', label: 'Cover Page' },
    { id: 'about', label: 'About Us' },
    { id: 'description', label: 'Description' },
    { id: 'areas', label: 'Areas & Items' },
    ...sortedAreas.map((a) => ({ id: `area-${a.id}`, label: a.name, sub: true })),
    { id: 'financial', label: 'Financial' },
    { id: 'payment', label: 'Payment Schedule' },
    { id: 'requests', label: 'Requests' },
    { id: 'profit', label: 'Profit' },
    { id: 'terms', label: 'Terms' },
  ]

  // Sidebar content (extracted for reuse in mobile tab)
  const sidebarContent = (
    <>
      {/* Summary Card */}
      <div className="pd-sidebar-card">
        <h3 className="pd-sidebar-card-title">Proposal Summary</h3>
        <div className="pd-summary-row"><span>Equipment</span><span className="mono">{fmtMoney(partsTotal)}</span></div>
        <div className="pd-summary-row"><span>Labor</span><span className="mono">{fmtMoney(laborTotal)}</span></div>
        <div className="pd-summary-row"><span>Tax</span><span className="mono">{fmtMoney(proposal.tax_amount)}</span></div>
        {(proposal.discount_amount ?? 0) > 0 && (
          <div className="pd-summary-row"><span>Discount</span><span className="mono" style={{ color: 'var(--danger)' }}>-{fmtMoney(proposal.discount_amount)}</span></div>
        )}
        <div className="pd-summary-divider" />
        <div className="pd-summary-total">
          <span>Total</span>
          <span className="pd-summary-total-value mono">{fmtMoney(proposal.total)}</span>
        </div>
      </div>

      {/* Client Card */}
      <div className="pd-sidebar-card" id="client" ref={setSectionRef('client')}>
        <h3 className="pd-sidebar-card-title">Client</h3>
        {client ? (
          <>
            <div className="pd-client-name">{client.contact_name || client.company_name}</div>
            {client.company_name && client.contact_name && (
              <div className="pd-client-detail">{client.company_name}</div>
            )}
            {(client.address_line1 || client.city) && (
              <div className="pd-client-detail">
                {[client.address_line1, client.city, client.state, client.zip].filter(Boolean).join(', ')}
              </div>
            )}
            {client.email && <div className="pd-client-detail">{client.email}</div>}
            {client.phone && <div className="pd-client-detail">{client.phone}</div>}
            <button className="pd-add-link" style={{ marginTop: 10, fontSize: 12 }} onClick={() => setShowClientPicker(true)}>
              Change client
            </button>
          </>
        ) : (
          <button className="pd-add-link" onClick={() => setShowClientPicker(true)}>+ Assign Client</button>
        )}
        {showClientPicker && (
          <div className="pd-client-picker">
            <input
              className="pd-client-search-input"
              placeholder="Search clients..."
              value={clientSearch}
              onChange={(e) => searchClients(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { setShowClientPicker(false); setClientSearch(''); setClientResults([]) } }}
              autoFocus
            />
            {searchingClients && <div className="pd-client-search-hint">Searching...</div>}
            {clientSearch.length > 0 && clientSearch.length < 2 && (
              <div className="pd-client-search-hint">Type at least 2 characters</div>
            )}
            {clientResults.length > 0 && (
              <div className="pd-client-results">
                {clientResults.map((c) => (
                  <button key={c.id} className="pd-client-result" onClick={() => assignClient(c.id)}>
                    <div className="pd-client-result-name">{c.contact_name || c.company_name}</div>
                    {c.company_name && c.contact_name && (
                      <div className="pd-client-result-company">{c.company_name}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
            <button className="pd-client-picker-cancel" onClick={() => { setShowClientPicker(false); setClientSearch(''); setClientResults([]) }}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* VMS Licensing Card */}
      {vmsRules.length > 0 && (
        <div className="pd-sidebar-card">
          <h3 className="pd-sidebar-card-title">VMS Platform</h3>
          <select
            value={proposal.vms_type || ''}
            onChange={(e) => handleVmsTypeChange(e.target.value || null)}
            disabled={vmsUpdating}
            className="input"
            style={{ fontSize: 13, width: '100%' }}
          >
            <option value="">None</option>
            {vmsRules.map(r => (
              <option key={r.vms_type} value={r.vms_type}>{r.vms_display_name}</option>
            ))}
          </select>
          {vmsUpdating && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Recalculating licenses...</div>
          )}
          {proposal.vms_type && !vmsUpdating && (() => {
            const licenseItems = (proposal.line_items ?? []).filter(li => li.item_type === 'license')
            const matchingRule = vmsRules.find(r => r.vms_type.toLowerCase() === (proposal.vms_type || '').toLowerCase())
            if (licenseItems.length === 0) {
              return (
                <p style={{ marginTop: 8, fontSize: 12, color: 'var(--warn)' }}>
                  {!matchingRule ? (
                    <>No license rules configured for this VMS type.{' '}
                    <a href="/settings/licensing" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Configure in Settings &rarr;</a></>
                  ) : (
                    <>No cameras/products with matching category found in this proposal. Add surveillance products to auto-calculate licenses.</>
                  )}
                </p>
              )
            }
            const totalLicenses = licenseItems.reduce((sum, li) => sum + li.quantity, 0)
            const totalCost = licenseItems.reduce((sum, li) => sum + (li.sell_price * li.quantity), 0)
            const cameraItems = (proposal.line_items ?? []).filter(li => li.item_type === 'product')
            const cameraCount = cameraItems.reduce((sum, li) => sum + li.quantity, 0)
            const licensingArea = (proposal.areas ?? []).find(a => a.name === 'Licensing')
            return (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ok)' }}>
                <div className="mono">{cameraCount} cameras &rarr; {totalLicenses} license{totalLicenses !== 1 ? 's' : ''} = {fmtMoney(totalCost)}</div>
                {licensingArea && (
                  <button
                    onClick={() => {
                      const el = document.getElementById(`area-${licensingArea.id}`)
                      if (el) el.scrollIntoView({ behavior: 'smooth' })
                    }}
                    style={{ fontSize: 11, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 4 }}
                  >
                    View in Licensing area
                  </button>
                )}
              </div>
            )
          })()}
        </div>
      )}

      {/* Client Link Card */}
      {shareUrl && (
        <div className="pd-sidebar-card pd-sidebar-card-accent">
          <h3 className="pd-sidebar-accent-title">Client Proposal Link</h3>
          <div className="pd-share-link-display">{shareUrl}</div>
          <div className="pd-share-link-actions">
            <button className="pd-share-link-btn" onClick={() => navigator.clipboard.writeText(shareUrl)}>Copy Link</button>
            <button className="pd-share-link-btn pd-share-link-btn-primary">Email Client</button>
          </div>
        </div>
      )}

      {/* Activity Card */}
      <div className="pd-sidebar-card">
        <h3 className="pd-sidebar-card-title">Activity</h3>
        {activity.length > 0 ? (
          activity.map((a) => (
            <div key={a.id} className="pd-activity-row">
              <div className="pd-activity-dot" />
              <div className="pd-activity-text">{a.detail || a.action}</div>
              <div className="pd-activity-time mono">{timeAgo(a.performed_at)}</div>
            </div>
          ))
        ) : (
          <div className="pd-activity-row">
            <div className="pd-activity-dot" />
            <div className="pd-activity-text">Proposal created</div>
            <div className="pd-activity-time mono">{timeAgo(proposal.created_at)}</div>
          </div>
        )}
      </div>
    </>
  )

  // Main content (extracted for reuse in mobile tab)
  const mainContent = (
    <>
      {/* Cover Page */}
      <CollapsibleSection
        id="cover"
        title="Cover Page"
        collapsed={collapsedSections.has('cover')}
        onToggle={() => toggleSection('cover')}
        refCb={setSectionRef('cover')}
      >
        <div className="pd-cover-content">
          {proposal.cover_image_r2_key ? (
            <div className="pd-cover-image overflow-hidden w-full">
              <img
                src={proposal.cover_image_r2_key.startsWith('http') ? proposal.cover_image_r2_key : `/api/files/${proposal.cover_image_r2_key}`}
                alt="Cover"
                className="w-full max-w-full h-auto object-cover rounded-lg"
                style={{ maxWidth: '100%' }}
              />
              <button className="pd-cover-change-btn" onClick={() => {
                setProposal((prev) => prev ? { ...prev, cover_image_r2_key: null } : prev)
                api.patch(`/proposals/${slug}`, { cover_image_r2_key: null })
              }}>
                Remove
              </button>
            </div>
          ) : (
            <div className="pd-cover-actions">
              <label className="pd-cover-placeholder">
                <span style={{ fontSize: 24 }}>🖼</span>
                <span>Upload cover image</span>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleCoverUpload(file)
                    e.target.value = ''
                  }}
                />
              </label>
              <div className="pd-cover-url-section">
                {showCoverInput ? (
                  <div className="pd-cover-url-form">
                    <input
                      className="pd-cover-url-input"
                      placeholder="Paste image URL..."
                      value={coverUrl}
                      onChange={(e) => setCoverUrl(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCoverUrl(); if (e.key === 'Escape') { setShowCoverInput(false); setCoverUrl('') } }}
                      autoFocus
                    />
                    <Button size="sm" onClick={handleCoverUrl}>Set</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setShowCoverInput(false); setCoverUrl('') }}>Cancel</Button>
                  </div>
                ) : (
                  <button className="pd-add-link" onClick={() => setShowCoverInput(true)}>Or use image URL</button>
                )}
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* About Us */}
      <CollapsibleSection
        id="about"
        title="About Us"
        collapsed={collapsedSections.has('about')}
        onToggle={() => toggleSection('about')}
        refCb={setSectionRef('about')}
      >
        {proposal.about_us ? (
          <EditableText value={proposal.about_us} onSave={(v) => handleFieldSave('about_us', v)} />
        ) : (
          <button className="pd-add-link" onClick={() => handleFieldSave('about_us', ' ')}>
            + Add About Us
          </button>
        )}
      </CollapsibleSection>

      {/* Project Description */}
      <CollapsibleSection
        id="description"
        title="Project Description"
        collapsed={collapsedSections.has('description')}
        onToggle={() => toggleSection('description')}
        refCb={setSectionRef('description')}
      >
        <div className="pd-desc-group">
          <div className="pd-desc-label">Project Description</div>
          {proposal.project_description ? (
            <EditableText value={proposal.project_description} onSave={(v) => handleFieldSave('project_description', v)} />
          ) : (
            <button className="pd-add-link" onClick={() => handleFieldSave('project_description', ' ')}>
              + Add Project Description
            </button>
          )}
        </div>
        <div className="pd-desc-group" style={{ marginTop: 16 }}>
          <div className="pd-desc-label">
            Internal / Installer Notes <span className="pd-hidden-tag">Hidden from client</span>
          </div>
          {proposal.installer_notes ? (
            <EditableText value={proposal.installer_notes} onSave={(v) => handleFieldSave('installer_notes', v)} />
          ) : (
            <button className="pd-add-link" onClick={() => handleFieldSave('installer_notes', ' ')}>
              + Add Internal / Installer Notes
            </button>
          )}
        </div>
      </CollapsibleSection>

      {/* Margin Warning Banner */}
      {marginProposalWarningEnabled && (lowMarginItems.length > 0 || belowCostItems.length > 0) && (
        <div
          style={{
            padding: '10px 16px',
            background: belowCostItems.length > 0 ? 'var(--err-m)' : 'var(--warn-m)',
            border: `1px solid ${belowCostItems.length > 0 ? 'var(--err)' : 'var(--warn)'}`,
            borderRadius: 8,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: belowCostItems.length > 0 ? 'var(--err)' : 'var(--warn)',
            cursor: 'pointer',
          }}
          onClick={() => scrollTo('areas')}
        >
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span>
            {lowMarginItems.length > 0 && (
              <>{lowMarginItems.length} item{lowMarginItems.length !== 1 ? 's' : ''} below {marginAlertThreshold}% margin</>
            )}
            {lowMarginItems.length > 0 && belowCostItems.length > 0 && ' · '}
            {belowCostItems.length > 0 && (
              <strong>{belowCostItems.length} item{belowCostItems.length !== 1 ? 's' : ''} below cost</strong>
            )}
          </span>
        </div>
      )}

      {/* Areas & Items */}
      <section id="areas" ref={setSectionRef('areas')} className="pd-areas-section">
        <div className="pd-section-header" onClick={() => toggleSection('areas')}>
          <SectionChevron collapsed={collapsedSections.has('areas')} />
          <h2 className="pd-section-title">Areas & Items</h2>
          <span className="pd-section-count">
            {sortedAreas.length} area{sortedAreas.length !== 1 ? 's' : ''}
          </span>
          <button
            className="pd-add-area-btn"
            onClick={(e) => { e.stopPropagation(); setAddingArea(true) }}
          >
            + Add Area
          </button>
        </div>
        {!collapsedSections.has('areas') && (
          <div className="pd-section-body">
            {/* Add Area Form */}
            {addingArea && (
              <div className="pd-add-area-form">
                <input
                  className="pd-add-area-input"
                  placeholder="Area name (e.g. Front Lobby, Server Room)"
                  value={newAreaName}
                  onChange={(e) => setNewAreaName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddArea(); if (e.key === 'Escape') { setAddingArea(false); setNewAreaName('') } }}
                  autoFocus
                />
                <Button className="pd-add-area-save" onClick={handleAddArea} disabled={savingArea || !newAreaName.trim()} loading={savingArea}>
                  Add
                </Button>
                <Button variant="ghost" className="pd-add-area-cancel" onClick={() => { setAddingArea(false); setNewAreaName('') }}>
                  Cancel
                </Button>
              </div>
            )}

            {sortedAreas.length === 0 && !addingArea ? (
              <div className="pd-empty-state">
                <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
                <div className="pd-empty-title">No areas defined for this proposal yet</div>
                <div className="pd-empty-desc">Areas organize your proposal by room or zone. Items are added within each area.</div>
                <Button
                  style={{ marginTop: 12 }}
                  onClick={() => setAddingArea(true)}
                >
                  + Add First Area
                </Button>
              </div>
            ) : (
              sortedAreas.map((area) => (
                <AreaCard
                  key={area.id}
                  area={area}
                  items={getItemsForArea(area.id)}
                  expanded={expandedAreas.has(area.id)}
                  activeOption={areaOptions[area.id] ?? 0}
                  proposalSlug={slug!}
                  onToggle={() => {
                    setExpandedAreas((prev) => {
                      const next = new Set(prev)
                      if (next.has(area.id)) next.delete(area.id)
                      else next.add(area.id)
                      return next
                    })
                  }}
                  onOptionChange={(idx) => setAreaOptions((prev) => ({ ...prev, [area.id]: idx }))}
                  onDelete={() => handleDeleteArea(area.slug, area.id)}
                  onAddItem={(item) => handleAddItem(area.id, item)}
                  onDeleteItem={(itemSlug, itemId) => handleDeleteItem(itemSlug, itemId)}
                  onApplyKit={(kitId) => handleApplyKit(kitId, area.slug)}
                  onApplyTemplate={(templateId) => handleApplyTemplate(templateId, area.slug)}
                  onSaveAsTemplate={(name) => handleSaveAsTemplate(area.slug, name)}
                  onUpdateItem={handleUpdateItem}
                  onReorderItems={handleReorderItems}
                  refCb={setSectionRef(`area-${area.id}`)}
                  onRefresh={refreshProposal}
                />
              ))
            )}
          </div>
        )}
      </section>

      {/* Financial Summary */}
      <CollapsibleSection
        id="financial"
        title="Financial Summary"
        collapsed={collapsedSections.has('financial')}
        onToggle={() => toggleSection('financial')}
        refCb={setSectionRef('financial')}
      >
        <div className="pd-financial">
          <div className="pd-fin-row">
            <span>Parts (Taxable)</span>
            <span className="mono">{fmtMoney(partsTotal - taxExemptTotal)}</span>
          </div>
          <div className="pd-fin-row">
            <span>Parts (Tax Exempt)</span>
            <span className="mono">{fmtMoney(taxExemptTotal)}</span>
          </div>
          <div className="pd-fin-row pd-fin-bold">
            <span>Total Parts</span>
            <span className="mono">{fmtMoney(partsTotal)}</span>
          </div>
          <div className="pd-fin-row">
            <span>Labor</span>
            <span className="mono">{fmtMoney(laborTotal)}</span>
          </div>
          <div className="pd-fin-divider" />
          <div className="pd-fin-row">
            <span>Subtotal</span>
            <span className="mono">{fmtMoney(proposal.subtotal)}</span>
          </div>
          {(proposal.discount_amount ?? 0) > 0 && (
            <div className="pd-fin-row">
              <span>Discount {proposal.discount_type === 'percent' ? `(${proposal.discount_value}%)` : ''}</span>
              <span className="mono" style={{ color: 'var(--danger)' }}>-{fmtMoney(proposal.discount_amount)}</span>
            </div>
          )}
          <div className="pd-fin-row">
            <span>Sales Tax {proposal.tax_rate ? `(${proposal.tax_rate}%)` : ''}</span>
            <span className="mono">{fmtMoney(proposal.tax_amount)}</span>
          </div>
          <div className="pd-fin-divider" />
          <div className="pd-fin-row pd-fin-total">
            <span>Proposal Total</span>
            <span className="mono pd-accent">{fmtMoney(proposal.total)}</span>
          </div>
        </div>
      </CollapsibleSection>

      {/* Payment Schedule */}
      <CollapsibleSection
        id="payment"
        title="Payment Schedule"
        collapsed={collapsedSections.has('payment')}
        onToggle={() => toggleSection('payment')}
        refCb={setSectionRef('payment')}
      >
        {(proposal.payments ?? []).length > 0 && (
          <div className="pd-payment-list">
            {(proposal.payments ?? []).map((p) => (
              <div key={p.id} className="pd-payment-row">
                <div className="pd-payment-label">{p.label}</div>
                <div className="pd-payment-type">
                  {p.amount_type === 'percent' ? `${p.amount_value}% of Total` :
                   p.amount_type === 'balance' ? 'Outstanding Balance' : 'Fixed Amount'}
                </div>
                <div className="pd-payment-trigger mono">
                  {p.trigger_type === 'date' && p.trigger_date ? fmtDate(p.trigger_date) : p.trigger_type || 'Milestone'}
                </div>
                <div className="pd-payment-amount mono">{fmtMoney(p.calculated_amount)}</div>
                <StatusBadge status={p.status} />
                <button
                  className="pd-item-delete-btn"
                  style={{ opacity: 1 }}
                  onClick={() => { if (confirm(`Delete "${p.label}"?`)) handleDeletePayment(p.id) }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
            <div className="pd-payment-footer">
              <span>Total Scheduled</span>
              <span className="mono" style={{ fontWeight: 600 }}>
                {fmtMoney((proposal.payments ?? []).reduce((s, p) => s + (p.calculated_amount ?? 0), 0))}
                {' / '}{fmtMoney(proposal.total)}
              </span>
            </div>
          </div>
        )}

        {/* Add Payment Form */}
        {addingPayment ? (
          <div className="pd-add-payment-form">
            <div className="pd-add-item-row">
              <input
                className="pd-add-item-input pd-add-item-name"
                placeholder="Payment label (e.g. Deposit, Upon Completion)"
                value={paymentForm.label}
                onChange={(e) => setPaymentForm((f) => ({ ...f, label: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddPayment(); if (e.key === 'Escape') setAddingPayment(false) }}
                autoFocus
              />
            </div>
            <div className="pd-add-item-row pd-add-item-numbers">
              <div className="pd-add-item-field">
                <label className="pd-add-item-label">Amount Type</label>
                <select
                  className="pd-add-item-select"
                  style={{ width: '100%' }}
                  value={paymentForm.amount_type}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, amount_type: e.target.value }))}
                >
                  <option value="percent">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                  <option value="balance">Remaining Balance</option>
                </select>
              </div>
              {paymentForm.amount_type !== 'balance' && (
                <div className="pd-add-item-field">
                  <label className="pd-add-item-label">{paymentForm.amount_type === 'percent' ? 'Percent (%)' : 'Amount ($)'}</label>
                  <input
                    className="pd-add-item-input"
                    type="number" step="0.01" min="0"
                    value={paymentForm.amount_value || ''}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, amount_value: parseFloat(e.target.value) || 0 }))}
                    placeholder={paymentForm.amount_type === 'percent' ? '50' : '0.00'}
                  />
                </div>
              )}
              <div className="pd-add-item-field">
                <label className="pd-add-item-label">Trigger</label>
                <select
                  className="pd-add-item-select"
                  style={{ width: '100%' }}
                  value={paymentForm.trigger_type}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, trigger_type: e.target.value }))}
                >
                  <option value="milestone">On Milestone</option>
                  <option value="date">Specific Date</option>
                  <option value="upon_approval">Upon Approval</option>
                  <option value="upon_completion">Upon Completion</option>
                </select>
              </div>
              {paymentForm.trigger_type === 'date' && (
                <div className="pd-add-item-field">
                  <label className="pd-add-item-label">Date</label>
                  <input
                    className="pd-add-item-input"
                    type="date"
                    value={paymentForm.trigger_date}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, trigger_date: e.target.value }))}
                  />
                </div>
              )}
            </div>
            <div className="pd-add-item-actions">
              <Button className="pd-add-item-save" onClick={handleAddPayment} disabled={savingPayment || !paymentForm.label.trim()} loading={savingPayment}>
                Add Payment
              </Button>
              <Button variant="ghost" onClick={() => setAddingPayment(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: (proposal.payments ?? []).length > 0 ? 8 : 0 }}>
            <button className="pd-add-link" onClick={() => setAddingPayment(true)}>
              + Add payment milestone
            </button>
          </div>
        )}
      </CollapsibleSection>

      {/* Payment Requests */}
      <CollapsibleSection
        id="requests"
        title="Payment Requests"
        collapsed={collapsedSections.has('requests')}
        onToggle={() => toggleSection('requests')}
        refCb={setSectionRef('requests')}
      >
        <div className="pd-empty-desc">No payment requests yet.</div>
      </CollapsibleSection>

      {/* Profit (dealer only) */}
      <CollapsibleSection
        id="profit"
        title="Profit"
        collapsed={collapsedSections.has('profit')}
        onToggle={() => toggleSection('profit')}
        refCb={setSectionRef('profit')}
      >
        <div className="pd-profit-grid">
          <ProfitStat label="Total Cost" value={fmtMoney(totalCost)} color="var(--text-secondary)" />
          <ProfitStat label="Total Sell" value={fmtMoney(totalSell)} color="var(--text)" />
          <ProfitStat label="Profit" value={fmtMoney(profit)} color={profit >= 0 ? 'var(--success)' : 'var(--danger)'} />
          <ProfitStat label="Margin" value={`${marginPct.toFixed(1)}%`}
            color={marginPct < 20 ? 'var(--danger)' : marginPct < 35 ? 'var(--warning)' : 'var(--success)'} />
        </div>
        {sortedAreas.length > 0 && (
          <div className="pd-profit-areas">
            <div className="pd-profit-area-header pd-desktop-only">
              <span>Area</span><span>Cost</span><span>Sell</span><span>Profit</span><span>Margin</span>
            </div>
            {sortedAreas.map((area) => {
              const aItems = getItemsForArea(area.id).filter((i) => !i.is_optional)
              const aCost = aItems.reduce((s, i) => s + i.cost * i.quantity, 0)
              const aSell = aItems.reduce((s, i) => s + i.sell_price * i.quantity, 0)
              const aProfit = aSell - aCost
              const aMargin = aSell > 0 ? ((aSell - aCost) / aSell) * 100 : 0
              return (
                <div key={area.id} className="pd-profit-area-row">
                  <span>{area.name}</span>
                  <span className="mono">{fmtMoney(aCost)}</span>
                  <span className="mono">{fmtMoney(aSell)}</span>
                  <span className="mono" style={{ color: aProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>{fmtMoney(aProfit)}</span>
                  <span className="mono" style={{ color: aMargin < 20 ? 'var(--danger)' : aMargin < 35 ? 'var(--warning)' : 'var(--success)' }}>{aMargin.toFixed(1)}%</span>
                </div>
              )
            })}
          </div>
        )}
      </CollapsibleSection>

      {/* Project Terms */}
      <CollapsibleSection
        id="terms"
        title="Project Terms"
        collapsed={collapsedSections.has('terms')}
        onToggle={() => toggleSection('terms')}
        refCb={setSectionRef('terms')}
      >
        {proposal.client_notes ? (
          <EditableText value={proposal.client_notes} onSave={(v) => handleFieldSave('client_notes', v)} />
        ) : (
          <button className="pd-add-link" onClick={() => handleFieldSave('client_notes', ' ')}>
            + Add Project Terms
          </button>
        )}
      </CollapsibleSection>
    </>
  )

  return (
    <div className="proposal-detail-page page-enter">
      {/* ── Top Bar ── */}
      <div className="pd-topbar">
        <div className="pd-topbar-left">
          <Breadcrumb items={[
            { label: 'Proposals', to: '/proposals' },
            ...(client ? [{ label: client.company_name, to: `/clients/${client.slug}` }] : []),
            { label: proposal.title },
          ]} />
          <div className="pd-title-row">
            <h1 className="pd-title">{proposal.title}</h1>
            <div className="pd-meta">
              <span className="pd-proposal-number">P-{proposal.slug.slice(0, 8).toUpperCase()}</span>
              <StatusBadge status={proposal.status} />
              <span className="pd-date mono">{fmtDate(proposal.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="pd-topbar-right">
          <div className="pd-total-block">
            <span className="pd-total-label">Total</span>
            <span className="pd-total-value mono">{fmtMoney(proposal.total)}</span>
          </div>
          <div className="pd-actions pd-desktop-only">
            <Button variant="secondary" onClick={handleClone} disabled={cloning} loading={cloning}>
              Clone
            </Button>
            <Button variant="secondary" onClick={generateToken}>
              {shareUrl ? 'Client Link' : 'Share'}
            </Button>
            <Button onClick={() => handleStatusChange('sent')}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              Send Proposal
            </Button>
            <div className="pd-menu-wrapper">
              <Button variant="ghost" className="pd-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>⋮</Button>
              {menuOpen && (
                <div className="pd-dropdown">
                  <div className="pd-dropdown-label">Change Status</div>
                  {STATUS_OPTIONS.map((s) => (
                    <button key={s} className="pd-dropdown-item" onClick={() => handleStatusChange(s)}>
                      {s.replace(/_/g, ' ')}
                    </button>
                  ))}
                  <div className="pd-dropdown-divider" />
                  <button className="pd-dropdown-item" onClick={previewClientView}>Preview (Client View)</button>
                  <button className="pd-dropdown-item" onClick={() => { setMenuOpen(false); setShowBranding(true) }}>Client Branding</button>
                  <button className="pd-dropdown-item" onClick={downloadPdf}>Download PDF</button>
                  <button className="pd-dropdown-item pd-dropdown-danger">Archive</button>
                </div>
              )}
            </div>
          </div>
          {/* Mobile menu trigger */}
          <div className="pd-mobile-only">
            <div className="pd-menu-wrapper">
              <Button variant="ghost" className="pd-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>⋮</Button>
              {menuOpen && (
                <div className="pd-dropdown">
                  <button className="pd-dropdown-item" onClick={handleClone} disabled={cloning}>
                    {cloning ? 'Cloning...' : 'Clone Proposal'}
                  </button>
                  <button className="pd-dropdown-item" onClick={generateToken}>
                    {shareUrl ? 'Copy Client Link' : 'Generate Share Link'}
                  </button>
                  <button className="pd-dropdown-item" onClick={() => { setMenuOpen(false); setShowBranding(true) }}>Client Branding</button>
                  <div className="pd-dropdown-divider" />
                  <div className="pd-dropdown-label">Change Status</div>
                  {STATUS_OPTIONS.map((s) => (
                    <button key={s} className="pd-dropdown-item" onClick={() => handleStatusChange(s)}>
                      {s.replace(/_/g, ' ')}
                    </button>
                  ))}
                  <div className="pd-dropdown-divider" />
                  <button className="pd-dropdown-item" onClick={previewClientView}>Preview (Client View)</button>
                  <button className="pd-dropdown-item" onClick={downloadPdf}>Download PDF</button>
                  <button className="pd-dropdown-item pd-dropdown-danger">Archive</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Client link banner */}
      {shareUrl && (
        <div className="pd-share-banner">
          <span className="pd-share-label">Client link:</span>
          <code className="pd-share-url">{shareUrl}</code>
          <Button variant="ghost" size="sm"
            onClick={() => navigator.clipboard.writeText(shareUrl)}>Copy</Button>
        </div>
      )}

      {/* ── Mobile Tab Bar ── */}
      <div className="pd-mobile-tabs pd-mobile-only">
        <button
          className={`pd-mobile-tab${mobileTab === 'content' ? ' active' : ''}`}
          onClick={() => setMobileTab('content')}
        >
          Proposal
        </button>
        <button
          className={`pd-mobile-tab${mobileTab === 'sidebar' ? ' active' : ''}`}
          onClick={() => setMobileTab('sidebar')}
        >
          Details
        </button>
        <button
          className="pd-mobile-tab"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
        >
          Sections
        </button>
      </div>

      {/* ── Mobile Section Nav (slide-down) ── */}
      {mobileNavOpen && (
        <div className="pd-mobile-nav pd-mobile-only">
          {anchorItems.map((item) => (
            <a
              key={item.id}
              className={`pd-mobile-nav-item${item.sub ? ' pd-mobile-nav-sub' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollTo(item.id); setMobileNavOpen(false); setMobileTab('content') }}
              href={`#${item.id}`}
            >
              {item.label}
            </a>
          ))}
        </div>
      )}

      {/* ── Three Column Layout (Desktop) / Tab Content (Mobile) ── */}
      <div className="pd-layout">
        {/* Left Anchor Nav — desktop only */}
        <nav className="pd-anchor-nav">
          {anchorItems.map((item) => (
            <a
              key={item.id}
              className={`pd-anchor-item${item.sub ? ' pd-anchor-sub' : ''}${activeSection === item.id ? ' active' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollTo(item.id) }}
              href={`#${item.id}`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Main Content */}
        <div className={`pd-main${mobileTab !== 'content' ? ' pd-mobile-hidden' : ''}`} ref={mainRef}>
          {mainContent}
        </div>

        {/* Right Sidebar */}
        <aside className={`pd-sidebar${mobileTab !== 'sidebar' ? ' pd-mobile-hidden' : ''}`}>
          {sidebarContent}
        </aside>
      </div>

      {/* ── Mobile Bottom Summary Bar ── */}
      <div className="pd-mobile-bottom-bar pd-mobile-only">
        <div className="pd-mobile-bottom-summary">
          <div className="pd-mobile-bottom-total-label">Total</div>
          <div className="pd-mobile-bottom-total-value mono">{fmtMoney(proposal.total)}</div>
        </div>
        <button className="pd-mobile-action-btn pd-mobile-action-primary" onClick={() => handleStatusChange('sent')}>
          Send Proposal
        </button>
      </div>

      {/* Close dropdown on backdrop click */}
      {menuOpen && <div className="pd-backdrop" onClick={() => setMenuOpen(false)} />}

      {/* Branding Override Modal */}
      {showBranding && (
        <BrandingOverrideModal
          currentOverride={(proposal as any)?.branding_override || null}
          onApply={handleBrandingApply}
          onClose={() => setShowBranding(false)}
        />
      )}
    </div>
  )
}

// ─── Sub Components ───

function SectionChevron({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      className={`pd-chevron${collapsed ? ' collapsed' : ''}`}
      width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={direction === 'left' ? { transform: 'rotate(90deg)' } : { transform: 'rotate(-90deg)' }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

function CollapsibleSection({ id, title, collapsed, onToggle, refCb, children }: {
  id: string; title: string; collapsed: boolean; onToggle: () => void
  refCb: (el: HTMLElement | null) => void; children: React.ReactNode
}) {
  return (
    <section id={id} ref={refCb} className="pd-section">
      <div className="pd-section-header" onClick={onToggle}>
        <SectionChevron collapsed={collapsed} />
        <h2 className="pd-section-title">{title}</h2>
      </div>
      {!collapsed && <div className="pd-section-body">{children}</div>}
    </section>
  )
}

function EditableText({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(value)

  if (!editing) {
    return (
      <div className="pd-editable-text" onClick={() => setEditing(true)}>
        {text.trim() || <span style={{ color: 'var(--text-tertiary)' }}>Click to edit...</span>}
      </div>
    )
  }

  return (
    <textarea
      className="pd-textarea"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => { setEditing(false); onSave(text) }}
      autoFocus
      rows={4}
    />
  )
}

function getItemImageUrl(item: ProposalLineItem): string | null {
  if (item.image_r2_key) return `/api/files/${item.image_r2_key}`
  if (item.custom_image_r2_key) return `/api/files/${item.custom_image_r2_key}`
  if (item.global_image_r2_key) return `/api/files/${item.global_image_r2_key}`
  if (item.global_image_url && item.global_image_url !== 'none' && item.global_image_url !== 'skip') return item.global_image_url
  return null
}

function ProductImage({ item }: { item: ProposalLineItem }) {
  const imgUrl = getItemImageUrl(item)
  if (imgUrl) {
    return <img className="item-image" src={imgUrl} alt={item.name} />
  }

  const brand = item.manufacturer || item.brand || ''
  const bg = item.item_type === 'labor' ? '#6b7280' : getBrandColor(brand)
  const initials = item.item_type === 'labor' ? '⚡' : (brand.slice(0, 3) || '?')

  return (
    <div className="item-image-placeholder" style={{ background: `linear-gradient(135deg, ${bg}dd, ${bg})` }}>
      {initials}
    </div>
  )
}

function MarginIndicator({ margin, threshold }: { margin: number; threshold: number }) {
  let color: string
  let label: string
  let icon: string
  if (margin <= 0) {
    color = 'var(--err)'
    label = `${margin.toFixed(1)}%`
    icon = '⚠️'
  } else if (margin < threshold) {
    color = 'var(--warn)'
    label = `${margin.toFixed(1)}%`
    icon = '⚠️'
  } else {
    color = 'var(--ok)'
    label = `${margin.toFixed(0)}%`
    icon = '✓'
  }
  return (
    <div className="item-margin">
      <span style={{ fontSize: 10 }}>{icon}</span>
      <span style={{ color }}>{label}</span>
    </div>
  )
}

interface GroupedItem extends ProposalLineItem {
  children: ProposalLineItem[]
}

function groupLineItems(items: ProposalLineItem[]): GroupedItem[] {
  const parents: ProposalLineItem[] = []
  const childMap = new Map<string, ProposalLineItem[]>()

  for (const item of items) {
    if (item.parent_item_id) {
      const children = childMap.get(item.parent_item_id) || []
      children.push(item)
      childMap.set(item.parent_item_id, children)
    } else {
      parents.push(item)
    }
  }

  return parents.map(parent => ({
    ...parent,
    children: childMap.get(parent.id) || [],
  }))
}

function OptionToggle({ labels, active, onChange }: { labels: string[]; active: number; onChange: (idx: number) => void }) {
  return (
    <div className="pd-option-toggle">
      {labels.map((label, idx) => (
        <button
          key={idx}
          className={`pd-option-btn${active === idx ? ' active' : ''}`}
          onClick={(e) => { e.stopPropagation(); onChange(idx) }}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

interface KitSummary { id: string; name: string; item_count: number; total_sell: number }
interface TemplateSummary { id: string; name: string; item_count: number; category: string | null }

function useMarginThreshold() {
  return useBrandingStore((s) => s.marginAlertThreshold)
}

function InlinePriceEdit({ value, itemSlug, proposalSlug, onUpdate }: {
  value: number; itemSlug: string; proposalSlug: string; onUpdate: (newPrice: number) => void
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(value.toFixed(2))

  function startEdit() {
    setEditValue(value.toFixed(2))
    setEditing(true)
  }

  async function savePrice() {
    const newPrice = parseFloat(editValue)
    if (isNaN(newPrice) || newPrice === value) {
      setEditing(false)
      return
    }
    try {
      await api.patch(`/proposals/${proposalSlug}/items/${itemSlug}`, { sell_price: newPrice })
      onUpdate(newPrice)
    } catch { /* ignore */ }
    setEditing(false)
  }

  if (editing) {
    return (
      <input
        className="price-input"
        type="number"
        step="0.01"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={savePrice}
        onKeyDown={(e) => { if (e.key === 'Enter') savePrice(); if (e.key === 'Escape') setEditing(false) }}
        autoFocus
      />
    )
  }

  return (
    <span className="price-display item-sell-price" onClick={startEdit}>
      {fmtMoney(value)}
    </span>
  )
}

function QuantityInput({ value, itemSlug, proposalSlug, onUpdate }: {
  value: number; itemSlug: string; proposalSlug: string; onUpdate: (newQty: number) => void
}) {
  const [localValue, setLocalValue] = useState(String(value))

  useEffect(() => { setLocalValue(String(value)) }, [value])

  async function saveQty() {
    const newQty = parseInt(localValue)
    if (isNaN(newQty) || newQty < 1 || newQty === value) {
      setLocalValue(String(value))
      return
    }
    try {
      await api.patch(`/proposals/${proposalSlug}/items/${itemSlug}`, { quantity: newQty })
      onUpdate(newQty)
    } catch { setLocalValue(String(value)) }
  }

  return (
    <input
      className="item-qty"
      type="number"
      min="1"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={saveQty}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
    />
  )
}

function DragHandle({ listeners, attributes }: { listeners: any; attributes: any }) {
  return (
    <button className="drag-handle" {...listeners} {...attributes}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <circle cx="5" cy="3" r="1.5" /><circle cx="11" cy="3" r="1.5" />
        <circle cx="5" cy="8" r="1.5" /><circle cx="11" cy="8" r="1.5" />
        <circle cx="5" cy="13" r="1.5" /><circle cx="11" cy="13" r="1.5" />
      </svg>
    </button>
  )
}

function SortableItemRow({ id, children }: { id: string; children: (props: { listeners: any; attributes: any; isDragging: boolean }) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative' as const,
  }
  return (
    <div ref={setNodeRef} style={style}>
      {children({ listeners, attributes, isDragging })}
    </div>
  )
}

function NestDropZone({ parentId, isOver }: { parentId: string; isOver: boolean }) {
  const { setNodeRef } = useDroppable({ id: `nest-${parentId}` })
  return (
    <div
      ref={setNodeRef}
      className={`nest-drop-zone${isOver ? ' nest-drop-active' : ''}`}
    >
      {isOver && <span className="nest-drop-label">Nest under this item</span>}
    </div>
  )
}

function UnnestDropZone({ isOver, visible }: { isOver: boolean; visible: boolean }) {
  const { setNodeRef } = useDroppable({ id: 'unnest-zone' })
  if (!visible) return null
  return (
    <div
      ref={setNodeRef}
      className={`unnest-drop-zone${isOver ? ' unnest-drop-active' : ''}`}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 15l-6-6-6 6" />
      </svg>
      <span>{isOver ? 'Drop to un-nest' : 'Drag here to un-nest'}</span>
    </div>
  )
}

function AttachmentSection({ children, proposalSlug: _proposalSlug, onToggleCombine, onUpdate: _onUpdate }: {
  children: ProposalLineItem[]; proposalSlug: string
  onToggleCombine: (itemId: string, slug: string, combine: boolean) => void
  onUpdate: (itemId: string, updates: Partial<ProposalLineItem>) => void
}) {
  const [expanded, setExpanded] = useState(false)
  if (children.length === 0) return null

  return (
    <div className="attachment-section">
      <div className="attachment-toggle" onClick={() => setExpanded(!expanded)}>
        <span className="attachment-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
          </svg>
        </span>
        <span>{children.length} Attachment{children.length !== 1 ? 's' : ''} Included</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
      {expanded && children.map((child) => (
        <SortableItemRow key={child.id} id={child.id}>
          {({ listeners, attributes }) => (
            <div className="attachment-content">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <DragHandle listeners={listeners} attributes={attributes} />
                <span style={{ fontSize: 14 }}>
                  {child.item_type === 'labor' ? '\u{1F527}' : '\u{1F4E6}'}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{child.name}</span>
                {child.item_type === 'labor' && <span className="labor-badge">LABOR</span>}
                {child.is_tax_exempt === 1 && <span className="tax-exempt-badge">Tax Exempt</span>}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-secondary)', marginLeft: 32 }}>
                {fmtMoney(child.sell_price)} &times; {child.quantity}
                {child.combine_prices === 1 ? (
                  <span style={{ marginLeft: 16, fontWeight: 500, color: 'var(--accent)', fontStyle: 'italic' }}>
                    incl. in product price
                  </span>
                ) : (
                  <span style={{ marginLeft: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
                    Total: {fmtMoney(child.sell_price * child.quantity)}
                  </span>
                )}
              </div>
              <label className="combine-toggle" style={{ marginLeft: 32 }}>
                <input
                  type="checkbox"
                  checked={child.combine_prices === 1}
                  onChange={(e) => onToggleCombine(child.id, child.slug, e.target.checked)}
                />
                Combine with product price
              </label>
            </div>
          )}
        </SortableItemRow>
      ))}
    </div>
  )
}

function ItemMoreMenu({ item, onDelete, onToggleTaxExempt }: {
  item: ProposalLineItem
  onDelete: () => void
  onToggleTaxExempt: () => void
}) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)

  function openMenu(e: React.MouseEvent) {
    e.stopPropagation()
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 4, left: rect.right })
    }
    setOpen((v) => !v)
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        className="item-more-btn"
        onClick={openMenu}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
        </svg>
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          <div
            className="pd-dropdown"
            style={{
              position: 'fixed',
              top: menuPos?.top ?? 0,
              left: (menuPos?.left ?? 0) - 180,
              right: 'auto',
              marginTop: 0,
              zIndex: 30,
              minWidth: 180,
            }}
          >
            <button className="pd-dropdown-item" onClick={() => { setOpen(false) }}>Edit Item</button>
            <button className="pd-dropdown-item" onClick={() => { setOpen(false) }}>Duplicate</button>
            <button className="pd-dropdown-item" onClick={() => { setOpen(false) }}>Move to Area</button>
            <button className="pd-dropdown-item" onClick={() => { setOpen(false) }}>Add Labor</button>
            <button className="pd-dropdown-item" onClick={() => { setOpen(false); onToggleTaxExempt() }}>
              {item.is_tax_exempt === 1 ? '✓ Tax Exempt' : 'Mark Tax Exempt'}
            </button>
            <div className="pd-dropdown-divider" />
            <button className="pd-dropdown-item pd-dropdown-danger" onClick={() => { setOpen(false); onDelete() }}>Remove from Proposal</button>
          </div>
        </>
      )}
    </div>
  )
}

function AreaCard({ area, items, expanded, activeOption, onToggle, onOptionChange, onDelete, onAddItem, onDeleteItem, onApplyKit, onApplyTemplate, onSaveAsTemplate, onUpdateItem, onReorderItems, proposalSlug, refCb, onRefresh }: {
  area: ProposalArea; items: ProposalLineItem[]; expanded: boolean; activeOption: number
  onToggle: () => void; onOptionChange: (idx: number) => void; onDelete: () => void
  onAddItem: (item: { name: string; item_type: string; quantity: number; cost: number; sell_price: number; description?: string; unit?: string; manufacturer?: string; mpn?: string; msrp?: number; catalog_item_id?: string; image_r2_key?: string }) => Promise<void>
  onDeleteItem: (itemSlug: string, itemId: string) => Promise<void>
  onApplyKit: (kitId: string) => Promise<void>
  onApplyTemplate: (templateId: string) => Promise<void>
  onSaveAsTemplate: (name: string) => Promise<void>
  onUpdateItem: (itemId: string, updates: Partial<ProposalLineItem>) => void
  onReorderItems: (reordered: { id: string; sort_order: number; parent_item_id: string | null; combine_prices?: number }[]) => Promise<void>
  proposalSlug: string
  refCb: (el: HTMLElement | null) => void
  onRefresh: () => Promise<void>
}) {
  const [showCatalogPicker, setShowCatalogPicker] = useState(false)
  const [addingItem, setAddingItem] = useState(false)
  const [savingItem, setSavingItem] = useState(false)
  const [showKitPicker, setShowKitPicker] = useState(false)
  const MARGIN_THRESHOLD = useMarginThreshold()
  const [showTemplatePicker, setShowTemplatePicker] = useState(false)
  const [kits, setKits] = useState<KitSummary[]>([])
  const [templates, setTemplates] = useState<TemplateSummary[]>([])
  const [applyingKit, setApplyingKit] = useState<string | null>(null)
  const [applyingTemplate, setApplyingTemplate] = useState<string | null>(null)
  const [showAreaMenu, setShowAreaMenu] = useState(false)
  const areaMenuBtnRef = useRef<HTMLButtonElement>(null)
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  const [templateName, setTemplateName] = useState(area.name)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [editingAreaName, setEditingAreaName] = useState(false)
  const [areaNameDraft, setAreaNameDraft] = useState(area.name)
  const [itemForm, setItemForm] = useState({
    name: '', item_type: 'product', quantity: 1, cost: 0, sell_price: 0, description: '', unit: 'ea',
  })

  async function loadKits() {
    try {
      const result = await api.get<KitSummary[]>('/kits')
      setKits(result)
    } catch { setKits([]) }
  }

  async function loadTemplates() {
    try {
      const result = await api.get<TemplateSummary[]>('/templates')
      setTemplates(result)
    } catch { setTemplates([]) }
  }

  async function handleApplyKit(kitId: string) {
    setApplyingKit(kitId)
    await onApplyKit(kitId)
    setApplyingKit(null)
    setShowKitPicker(false)
  }

  async function handleApplyTemplate(templateId: string) {
    setApplyingTemplate(templateId)
    await onApplyTemplate(templateId)
    setApplyingTemplate(null)
    setShowTemplatePicker(false)
  }

  async function handleSaveAsTemplate() {
    if (!templateName.trim()) return
    setSavingTemplate(true)
    await onSaveAsTemplate(templateName)
    setSavingTemplate(false)
    setShowSaveTemplate(false)
  }

  async function handleToggleCombine(itemId: string, itemSlug: string, combine: boolean) {
    try {
      await api.patch(`/proposals/${proposalSlug}/items/${itemSlug}`, { combine_prices: combine ? 1 : 0 })
      onUpdateItem(itemId, { combine_prices: combine ? 1 : 0 })
    } catch { /* ignore */ }
  }

  const { toastError } = useToast()

  async function handleToggleTaxExempt(item: ProposalLineItem) {
    const newVal = item.is_tax_exempt === 1 ? 0 : 1
    // Optimistic update
    onUpdateItem(item.id, { is_tax_exempt: newVal })
    try {
      await api.patch(`/proposals/${proposalSlug}/items/${item.slug}`, { is_tax_exempt: newVal })
      // Refresh to get recalculated totals (tax_amount, total) from backend
      await onRefresh()
    } catch (err) {
      // Revert optimistic update
      onUpdateItem(item.id, { is_tax_exempt: item.is_tax_exempt })
      toastError('Failed to update tax exempt status', err)
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  )

  const [nestOverId, setNestOverId] = useState<string | null>(null)
  const [unnestActive, setUnnestActive] = useState(false)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)

  const optionLabels: string[] = area.option_labels ? JSON.parse(area.option_labels) : []
  const hasOptions = optionLabels.length > 1

  const filteredItems = hasOptions
    ? items.filter((i) => i.option_index === activeOption)
    : items

  const sortedItems = [...filteredItems].sort((a, b) => a.sort_order - b.sort_order)

  // Group items: parents with nested children
  const grouped = groupLineItems(sortedItems)

  // All draggable IDs: parents + children (for un-nesting)
  const allDraggableIds = grouped.flatMap((g) => [g.id, ...g.children.map((c) => c.id)])

  // Check if an item is currently a nested child
  function isNestedChild(itemId: string): boolean {
    return grouped.some((g) => g.children.some((c) => c.id === itemId))
  }

  function handleDragStart(event: { active: { id: string | number } }) {
    setActiveDragId(event.active.id as string)
  }

  function handleDragOver(event: DragOverEvent) {
    const overId = event.over?.id as string | undefined
    if (overId && typeof overId === 'string' && overId.startsWith('nest-')) {
      setNestOverId(overId.replace('nest-', ''))
      setUnnestActive(false)
    } else if (overId === 'unnest-zone') {
      setUnnestActive(true)
      setNestOverId(null)
    } else {
      setNestOverId(null)
      setUnnestActive(false)
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    setNestOverId(null)
    setUnnestActive(false)
    setActiveDragId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeId = active.id as string
    const overId = over.id as string

    // Case 1: Dropping onto a nest zone → nest under that parent
    if (overId.startsWith('nest-')) {
      const targetParentId = overId.replace('nest-', '')
      // Don't nest under self
      if (targetParentId === activeId) return

      const updates: { id: string; sort_order: number; parent_item_id: string | null }[] = []

      // Set parent_item_id on the dragged item
      const targetParent = grouped.find((g) => g.id === targetParentId)
      if (!targetParent) return

      const existingChildren = targetParent.children.length
      updates.push({
        id: activeId,
        sort_order: existingChildren,
        parent_item_id: targetParentId,
      })

      // If the dragged item was a parent with children, move children to top level
      const draggedParent = grouped.find((g) => g.id === activeId)
      if (draggedParent) {
        for (const child of draggedParent.children) {
          updates.push({
            id: child.id,
            sort_order: child.sort_order,
            parent_item_id: null,
          })
        }
      }

      // Recalculate sort_order for remaining parents
      const remainingParents = grouped.filter((g) => g.id !== activeId)
      remainingParents.forEach((p, idx) => {
        updates.push({ id: p.id, sort_order: idx, parent_item_id: null })
        for (const child of p.children) {
          if (child.id !== activeId) {
            updates.push({ id: child.id, sort_order: child.sort_order, parent_item_id: child.parent_item_id ?? null })
          }
        }
      })

      await onReorderItems(updates)
      return
    }

    // Case 2: Un-nesting — a child dropped onto the main list or un-nest zone
    const activeIsChild = isNestedChild(activeId)

    if (activeIsChild && (overId === 'unnest-zone' || grouped.some((g) => g.id === overId))) {
      const overIndex = overId === 'unnest-zone'
        ? grouped.length  // append to end
        : grouped.findIndex((g) => g.id === overId)
      if (overIndex === -1) return

      const updates: { id: string; sort_order: number; parent_item_id: string | null; combine_prices?: number }[] = []

      // The un-nested item: clear parent + combine_prices
      const unnestedItem = sortedItems.find((i) => i.id === activeId)
      if (!unnestedItem) return

      // Build new parent list with the un-nested item inserted
      const newParents = [...grouped]
      newParents.splice(overIndex, 0, { ...unnestedItem, parent_item_id: null, combine_prices: 0, children: [] } as GroupedItem)

      newParents.forEach((p, idx) => {
        if (p.id === activeId) {
          // This is the un-nested item
          updates.push({ id: activeId, sort_order: idx, parent_item_id: null, combine_prices: 0 })
        } else {
          updates.push({ id: p.id, sort_order: idx, parent_item_id: null })
          for (const child of (p.children || [])) {
            if (child.id !== activeId) {
              updates.push({ id: child.id, sort_order: child.sort_order, parent_item_id: child.parent_item_id ?? null })
            }
          }
        }
      })

      await onReorderItems(updates)
      return
    }

    // Case 3: Normal parent reorder
    const oldIndex = grouped.findIndex((g) => g.id === activeId)
    const newIndex = grouped.findIndex((g) => g.id === overId)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(grouped, oldIndex, newIndex)
    const updates: { id: string; sort_order: number; parent_item_id: string | null }[] = []

    reordered.forEach((item, idx) => {
      updates.push({ id: item.id, sort_order: idx, parent_item_id: item.parent_item_id ?? null })
      for (const child of item.children) {
        updates.push({ id: child.id, sort_order: child.sort_order, parent_item_id: child.parent_item_id ?? null })
      }
    })

    await onReorderItems(updates)
  }

  const areaTotal = filteredItems
    .filter((i) => !i.is_optional)
    .reduce((s, i) => s + i.sell_price * i.quantity, 0)

  async function submitItem() {
    if (!itemForm.name.trim()) return
    setSavingItem(true)
    await onAddItem(itemForm)
    setItemForm({ name: '', item_type: 'product', quantity: 1, cost: 0, sell_price: 0, description: '', unit: 'ea' })
    setAddingItem(false)
    setSavingItem(false)
  }

  function getCombinedUnitPrice(item: GroupedItem): number {
    let unitPrice = item.sell_price
    for (const child of item.children) {
      if (child.combine_prices === 1) {
        unitPrice += child.sell_price
      }
    }
    return unitPrice
  }

  function getItemTotal(item: GroupedItem): number {
    return getCombinedUnitPrice(item) * item.quantity
  }

  function hasCombinedChildren(item: GroupedItem): boolean {
    return item.children.some((c) => c.combine_prices === 1)
  }

  function getCombinedChildNames(item: GroupedItem): string[] {
    return item.children.filter((c) => c.combine_prices === 1).map((c) => c.name)
  }

  return (
    <div className="pd-area-card" id={`area-${area.id}`} ref={refCb}>
      {/* Area Header */}
      <div className="area-header" onClick={onToggle}>
        <SectionChevron collapsed={!expanded} />
        <span className="area-name">
          {editingAreaName ? (
            <input
              className="pd-add-area-input"
              value={areaNameDraft}
              onChange={(e) => setAreaNameDraft(e.target.value)}
              onBlur={() => setEditingAreaName(false)}
              onKeyDown={(e) => { if (e.key === 'Enter') setEditingAreaName(false); if (e.key === 'Escape') { setAreaNameDraft(area.name); setEditingAreaName(false) } }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              style={{ fontSize: 18, fontWeight: 800, border: 'none', borderBottom: '2px solid var(--accent)', outline: 'none', background: 'transparent', padding: 0, width: 'auto' }}
            />
          ) : area.name}
        </span>
        {hasOptions && (
          <OptionToggle labels={optionLabels} active={activeOption} onChange={onOptionChange} />
        )}
        <div className="area-item-count">{filteredItems.length} ITEM{filteredItems.length !== 1 ? 'S' : ''}</div>
        <div className="area-actions">
          <button
            className="pd-add-item-header-btn"
            onClick={(e) => { e.stopPropagation(); setShowCatalogPicker(true) }}
            title="Add item"
          >
            +
          </button>
          <div style={{ position: 'relative' }}>
            <button
              ref={areaMenuBtnRef}
              className="pd-area-delete-btn"
              onClick={(e) => { e.stopPropagation(); setShowAreaMenu((v) => !v) }}
              title="Area options"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
              </svg>
            </button>
            {showAreaMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 19 }} onClick={(e) => { e.stopPropagation(); setShowAreaMenu(false) }} />
                <div className="pd-dropdown" style={{ position: 'fixed', top: (areaMenuBtnRef.current?.getBoundingClientRect().bottom ?? 0) + 4, left: (areaMenuBtnRef.current?.getBoundingClientRect().right ?? 0) - 200, right: 'auto', marginTop: 0, zIndex: 20, minWidth: 200 }} onClick={(e) => e.stopPropagation()}>
                  <button className="pd-dropdown-item" onClick={() => { setShowAreaMenu(false); setEditingAreaName(true); setAreaNameDraft(area.name) }}>Rename Area</button>
                  <button className="pd-dropdown-item" onClick={() => setShowAreaMenu(false)}>Add Description</button>
                  <button className="pd-dropdown-item" onClick={() => setShowAreaMenu(false)}>Add Internal Note</button>
                  <div className="pd-dropdown-divider" />
                  <button className="pd-dropdown-item" onClick={() => { setShowAreaMenu(false); setShowKitPicker(true); loadKits() }}>Apply Kit</button>
                  <button className="pd-dropdown-item" onClick={() => { setShowAreaMenu(false); setTemplateName(area.name); setShowSaveTemplate(true) }}>Save as Template</button>
                  <button className="pd-dropdown-item" onClick={() => { setShowAreaMenu(false); setShowTemplatePicker(true); loadTemplates() }}>Load from Template</button>
                  <div className="pd-dropdown-divider" />
                  <button className="pd-dropdown-item" onClick={() => setShowAreaMenu(false)}>Add Option (Good/Better/Best)</button>
                  <button className="pd-dropdown-item" onClick={() => setShowAreaMenu(false)}>Clone Area</button>
                  <div className="pd-dropdown-divider" />
                  <button className="pd-dropdown-item" onClick={() => setShowAreaMenu(false)}>Move Up</button>
                  <button className="pd-dropdown-item" onClick={() => setShowAreaMenu(false)}>Move Down</button>
                  <div className="pd-dropdown-divider" />
                  <button className="pd-dropdown-item pd-dropdown-danger" onClick={() => { setShowAreaMenu(false); if (confirm(`Delete area "${area.name}"?`)) onDelete() }}>Delete Area</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <>
          {(area.client_description || area.internal_note) && (
            <div className="pd-area-desc">
              {area.client_description && <p>{area.client_description}</p>}
              {area.internal_note && <p className="pd-area-internal-note">Internal: {area.internal_note}</p>}
            </div>
          )}

          {/* Column headers */}
          {grouped.length > 0 && (
            <div className="items-column-headers">
              <span style={{ flex: 1 }}>ITEM</span>
              <span style={{ width: 100, textAlign: 'right' }}>SELL PRICE</span>
              <span style={{ width: 60, textAlign: 'center' }}>QTY</span>
              <span style={{ width: 100, textAlign: 'right' }}>TOTAL</span>
              <span style={{ width: 32 }} />
            </div>
          )}

          {/* Line items */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
            <UnnestDropZone isOver={unnestActive} visible={!!activeDragId && isNestedChild(activeDragId)} />
            <SortableContext items={allDraggableIds} strategy={verticalListSortingStrategy}>
              {grouped.map((item) => (
                <div key={item.id}>
                  <SortableItemRow id={item.id}>
                    {({ listeners, attributes, isDragging }) => (
                      <div className={`item-row-wrapper${isDragging ? ' is-dragging' : ''}`}>
                        <div className="item-row">
                          <DragHandle listeners={listeners} attributes={attributes} />
                          <ProductImage item={item} />
                          <div className="item-info">
                            <div className="item-brand-line">
                              {(item.manufacturer || item.brand) && (
                                <span className="brand">{item.manufacturer || item.brand}</span>
                              )}
                              {(item.mpn || item.global_mpn) && (
                                <span className="mpn"> &middot; {item.mpn || item.global_mpn}</span>
                              )}
                              {item.item_type === 'labor' && <span className="labor-badge" style={{ marginLeft: 6 }}>LABOR</span>}
                              {item.is_tax_exempt === 1 && <span className="tax-exempt-badge" style={{ marginLeft: 6 }}>Tax Exempt</span>}
                            </div>
                            <div className="item-name">{item.name}</div>
                            {item.description && <div className="item-description">{item.description}</div>}
                          </div>
                          <div className="item-pricing-col">
                            {hasCombinedChildren(item) ? (
                              <>
                                <span className="price-display item-sell-price mono">{fmtMoney(getCombinedUnitPrice(item))}</span>
                                <div className="item-combined-incl">incl. {getCombinedChildNames(item).join(', ')}</div>
                              </>
                            ) : (
                              <>
                                {item.msrp > 0 && item.msrp !== item.sell_price && (
                                  <div className="item-msrp mono">msrp {fmtMoney(item.msrp)}</div>
                                )}
                                <InlinePriceEdit
                                  value={item.sell_price}
                                  itemSlug={item.slug}
                                  proposalSlug={proposalSlug}
                                  onUpdate={(newPrice) => onUpdateItem(item.id, { sell_price: newPrice })}
                                />
                                <MarginIndicator margin={item.margin_percent} threshold={MARGIN_THRESHOLD} />
                              </>
                            )}
                          </div>
                          <div className="item-qty-col">
                            <QuantityInput
                              value={item.quantity}
                              itemSlug={item.slug}
                              proposalSlug={proposalSlug}
                              onUpdate={(newQty) => onUpdateItem(item.id, { quantity: newQty })}
                            />
                          </div>
                          <div className="item-total-col">
                            <div className="item-total mono">{fmtMoney(getItemTotal(item))}</div>
                            {hasCombinedChildren(item) && (
                              <div className="item-combined-label">Combined</div>
                            )}
                          </div>
                          <ItemMoreMenu
                            item={item}
                            onDelete={() => { if (confirm(`Remove "${item.name}"?`)) onDeleteItem(item.slug, item.id) }}
                            onToggleTaxExempt={() => handleToggleTaxExempt(item)}
                          />
                        </div>

                        {/* Nested labor / attachments */}
                        <AttachmentSection
                          children={item.children}
                          proposalSlug={proposalSlug}
                          onToggleCombine={handleToggleCombine}
                          onUpdate={(itemId, updates) => onUpdateItem(itemId, updates)}
                        />
                      </div>
                    )}
                  </SortableItemRow>

                  {/* Nest drop zone below each parent */}
                  <NestDropZone parentId={item.id} isOver={nestOverId === item.id} />
                </div>
              ))}
            </SortableContext>
          </DndContext>

          {/* Empty state */}
          {grouped.length === 0 && !addingItem && (
            <div className="area-empty-state">
              <div className="area-empty-title">No line items yet</div>
              <div className="area-empty-actions">
                <button className="add-item-link" onClick={() => setShowCatalogPicker(true)}>+ Add from Catalog</button>
                <button className="add-item-link" onClick={() => { setShowKitPicker(true); loadKits() }}>Apply Kit</button>
                <button className="add-item-link" onClick={() => { setShowTemplatePicker(true); loadTemplates() }}>Load Template</button>
              </div>
            </div>
          )}

          {/* Add Item Form */}
          {addingItem && (
            <div className="pd-add-item-form">
              <div className="pd-add-item-row">
                <select
                  className="pd-add-item-select"
                  value={itemForm.item_type}
                  onChange={(e) => setItemForm((f) => ({ ...f, item_type: e.target.value }))}
                >
                  <option value="product">Product</option>
                  <option value="labor">Labor</option>
                  <option value="service">Service</option>
                  <option value="misc">Misc</option>
                </select>
                <input
                  className="pd-add-item-input pd-add-item-name"
                  placeholder="Item name"
                  value={itemForm.name}
                  onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitItem(); if (e.key === 'Escape') setAddingItem(false) }}
                  autoFocus
                />
              </div>
              <div className="pd-add-item-row">
                <input
                  className="pd-add-item-input pd-add-item-desc"
                  placeholder="Description (optional)"
                  value={itemForm.description}
                  onChange={(e) => setItemForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="pd-add-item-row pd-add-item-numbers">
                <div className="pd-add-item-field">
                  <label className="pd-add-item-label">Cost</label>
                  <input
                    className="pd-add-item-input"
                    type="number" step="0.01" min="0"
                    value={itemForm.cost || ''}
                    onChange={(e) => setItemForm((f) => ({ ...f, cost: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                  />
                </div>
                <div className="pd-add-item-field">
                  <label className="pd-add-item-label">Sell Price</label>
                  <input
                    className="pd-add-item-input"
                    type="number" step="0.01" min="0"
                    value={itemForm.sell_price || ''}
                    onChange={(e) => setItemForm((f) => ({ ...f, sell_price: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                  />
                </div>
                <div className="pd-add-item-field">
                  <label className="pd-add-item-label">Qty</label>
                  <input
                    className="pd-add-item-input"
                    type="number" step="1" min="1"
                    value={itemForm.quantity}
                    onChange={(e) => setItemForm((f) => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                  />
                </div>
                <div className="pd-add-item-field">
                  <label className="pd-add-item-label">Unit</label>
                  <input
                    className="pd-add-item-input"
                    value={itemForm.unit}
                    onChange={(e) => setItemForm((f) => ({ ...f, unit: e.target.value }))}
                    placeholder="ea"
                  />
                </div>
              </div>
              <div className="pd-add-item-actions">
                <Button className="pd-add-item-save" onClick={submitItem} disabled={savingItem || !itemForm.name.trim()} loading={savingItem}>
                  Add Item
                </Button>
                <Button variant="ghost" onClick={() => setAddingItem(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Area footer */}
          <div className="area-footer">
            <div className="area-footer-left">
              {!addingItem && (
                <button className="add-item-link" onClick={() => setShowCatalogPicker(true)}>+ Add item</button>
              )}
            </div>
            <div className="area-footer-right">
              <span className="area-total-label">Area Total</span>
              <span className="area-total-value mono">{fmtMoney(areaTotal)}</span>
            </div>
          </div>
        </>
      )}

      <CatalogPickerModal
        isOpen={showCatalogPicker}
        onClose={() => setShowCatalogPicker(false)}
        onSelect={async (catalogItem) => {
          setShowCatalogPicker(false)
          await onAddItem({
            name: catalogItem.custom_name || catalogItem.name || 'Unknown',
            item_type: catalogItem.item_type === 'labor' ? 'labor' : 'product',
            quantity: 1,
            cost: catalogItem.cost,
            sell_price: catalogItem.sell_price,
            description: catalogItem.custom_description || catalogItem.description || '',
            unit: 'ea',
            manufacturer: catalogItem.custom_brand || catalogItem.brand || undefined,
            mpn: catalogItem.custom_mpn || catalogItem.mpn || undefined,
            msrp: catalogItem.msrp,
            catalog_item_id: catalogItem.id,
            image_r2_key: catalogItem.custom_image_r2_key || catalogItem.image_r2_key || undefined,
          })
        }}
        onCustomItem={() => {
          setShowCatalogPicker(false)
          setAddingItem(true)
        }}
      />

      {/* Kit Picker Modal */}
      {showKitPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--bg-overlay, rgba(0,0,0,0.5))', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setShowKitPicker(false)}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl, 16px)', boxShadow: 'var(--shadow-lg)', maxWidth: 500, width: '100%', maxHeight: '60vh', overflow: 'auto', animation: 'modal-in 0.2s ease' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Apply Kit to Area</h3>
              <button onClick={() => setShowKitPicker(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-tertiary)', cursor: 'pointer' }}>&times;</button>
            </div>
            {kits.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 14 }}>
                No kits available. Create kits in Settings.
              </div>
            ) : (
              kits.map((kit) => (
                <div key={kit.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid var(--border-light, var(--border))' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{kit.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{kit.item_count} items &middot; {formatCurrency(kit.total_sell)}</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleApplyKit(kit.id)}
                    disabled={applyingKit === kit.id}
                    loading={applyingKit === kit.id}
                  >
                    Apply
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--bg-overlay, rgba(0,0,0,0.5))', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setShowTemplatePicker(false)}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl, 16px)', boxShadow: 'var(--shadow-lg)', maxWidth: 500, width: '100%', maxHeight: '60vh', overflow: 'auto', animation: 'modal-in 0.2s ease' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Load Area Template</h3>
              <button onClick={() => setShowTemplatePicker(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-tertiary)', cursor: 'pointer' }}>&times;</button>
            </div>
            {templates.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 14 }}>
                No templates available. Save an area as a template first.
              </div>
            ) : (
              templates.map((tpl) => (
                <div key={tpl.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid var(--border-light, var(--border))' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{tpl.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {tpl.item_count} items
                      {tpl.category && <span> &middot; {tpl.category}</span>}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleApplyTemplate(tpl.id)}
                    disabled={applyingTemplate === tpl.id}
                    loading={applyingTemplate === tpl.id}
                  >
                    Apply
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Save as Template Modal */}
      {showSaveTemplate && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--bg-overlay, rgba(0,0,0,0.5))', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setShowSaveTemplate(false)}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl, 16px)', boxShadow: 'var(--shadow-lg)', maxWidth: 400, width: '100%', padding: 24, animation: 'modal-in 0.2s ease' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Save Area as Template</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Template Name</label>
              <input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="input"
                autoFocus
                style={{ fontSize: 14 }}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowSaveTemplate(false)} style={{ padding: '8px 16px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <Button
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate || !templateName.trim()}
                loading={savingTemplate}
              >
                Save Template
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

function ProfitStat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="pd-profit-stat">
      <div className="pd-profit-stat-label">{label}</div>
      <div className="pd-profit-stat-value mono" style={{ color }}>{value}</div>
    </div>
  )
}
